import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "./surface-contract";

/**
 * TextInput — Tahoe recipe (approved 2026-06-10, shape split 2026-06-11).
 *
 * .pen primitives mapped:
 *   ui/input-default  → resting state: glass-fill-heavy, glass-stroke-light, backdrop-blur
 *   ui/input-focused  → focus state: accent border, glow shadow
 *   ui/input-filled   → filled state: glass-fill-dense, slightly stronger border
 *
 * The "input" surface role (fill/stroke/blur) is applied inline on the element
 * so the surface contract can pair it with the role attribute; this recipe
 * carries geometry/typography only. Shared by PasswordInput — no duplicate
 * recipe.
 */
const textInputVariants = cva(
  cn(
    "h-[36px] w-full text-appkit-body text-primary placeholder:text-muted transition-shadow transition-colors",
    "focus:border-accent focus:shadow-input-focus focus:outline-none",
    "disabled:cursor-not-allowed disabled:opacity-40",
  ),
  {
    variants: {
      /**
       * macOS Tahoe input shapes:
       *  - "field" (default) — rounded-rect, the form idiom: login, settings,
       *    anything stacked with labels.
       *  - "capsule" — the search/composer idiom: search modal, input bar;
       *    18px inset matches the capsule radius like buttons.
       */
      shape: {
        field: "rounded-control px-appkit-sm",
        capsule:
          "rounded-pill px-[18px] [--radius-surface-input:var(--appkit-radius-pill)] [--denk-surface-radius-input:var(--appkit-radius-pill)]",
      },
    },
    defaultVariants: { shape: "field" },
  },
);

type TextInputProps = React.ComponentProps<"input"> &
  VariantProps<typeof textInputVariants> & {
    "data-testid"?: string;
  };

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ className, type = "text", shape, ...props }, ref) => {
    return (
      <input
        ref={ref}
        {...props}
        type={type}
        className={cn(
          surfaceRoleClassName("input"),
          textInputVariants({ shape }),
          className,
        )}
        {...surfaceRoleAttributes("input", { nativeLevel: "child" })}
      />
    );
  },
);
TextInput.displayName = "TextInput";

export { TextInput, textInputVariants };
export type { TextInputProps };
